import { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import AssetsListBox from "./AssetsListBox";
import AssetsListDropGap from "./AssetsListDropGap";

// Strips the y-axis from drag transforms so boxes in a horizontal row don't
// lift vertically while being reordered.
const restrictToHorizontalAxis = ({ transform }) => ({
  ...transform,
  y: 0,
});

// `min-h-20` is load-bearing: with an empty list there are no boxes to give the
// row a height, so without it the row collapses to its padding and every drop
// gap — being `h-full` of nothing — becomes 0px tall and cannot be dropped on.
export default function AssetsListRow({
  boxes,
  resolveAssetById,
  onInsertAssetsAt,
  onMoveEntry,
  onRemoveEntry,
  onOpenEntryPreview,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const sortableIds = useMemo(() => boxes.map((box) => box.entryKey), [boxes]);

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;

    const fromIndex = sortableIds.indexOf(active.id);
    const toIndex = sortableIds.indexOf(over.id);
    if (fromIndex === -1) return;
    if (toIndex === -1) return;

    onMoveEntry({ fromIndex, toIndex });
  };

  return (
    <div
      id="AssetsList__row"
      className="AssetsList__row relative flex items-stretch flex-1 min-w-0 h-full min-h-20 overflow-x-auto overflow-y-hidden py-1 pl-[1.2rem]">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}>
        <SortableContext
          items={sortableIds}
          strategy={horizontalListSortingStrategy}>
          {boxes.map((box, index) => (
            <div key={box.entryKey} className="flex h-full">
              <AssetsListDropGap
                index={index}
                resolveAssetById={resolveAssetById}
                onInsertAssetsAt={onInsertAssetsAt}
              />
              <AssetsListBox
                entryKey={box.entryKey}
                assetId={box.assetId}
                asset={box.asset}
                isPreviewing={box.isPreviewing}
                onRemoveEntry={onRemoveEntry}
                onOpenEntryPreview={onOpenEntryPreview}
              />
            </div>
          ))}
        </SortableContext>
      </DndContext>

      <AssetsListDropGap
        index={boxes.length}
        isTrailing
        resolveAssetById={resolveAssetById}
        onInsertAssetsAt={onInsertAssetsAt}
      />

      {boxes.length === 0 && (
        <span className="AssetsList__emptyHint pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-secondary-foreground/50">
          Drag assets here to build a list
        </span>
      )}
    </div>
  );
}
